import memoize from 'memoizee';
import config from '@/constants/config';
import { GraphQLClient, gql } from 'graphql-request'
import { multiCall } from '@/utils/Calls';
import { sleepUntil } from '@/utils/Async';
import Request from '@/utils/Request';
import { getGaugeDataForAddress } from '@/utils/data/getAllGauges';
import { votingescrow_address, votingescrow_abi, ERC20_abi } from '@/constants/abis';
import { contract } from '../../contract'
import getPricesForAssets from './getPricesForAssets';
import getPoolUsdFigure from './getPoolUsdFigure';
import { isFactoryAbiDataLoaded } from '@/constants/abis';
import {
  findPoolForSwapAddress,
  getNonFactoPools,
  getNonFactoPoolGauges,
  getFactoryV2Pools,
  getFactoryCryptoPools,
  isFactoryPoolDataLoaded,
} from '@/constants/pools'

// NOTE: DEFAULTING TO virtual_price = 1 FOR FACTORY POOLS FOR NOW, REVERTING OTHERWISE
const getCRVAPY = memoize(async (userAddress, includeFactoryV2 = false) => {
  await sleepUntil(() => isFactoryAbiDataLoaded() && isFactoryPoolDataLoaded());

  let gaugeController_address = '0x2F50D538606Fa9EDD2B11E2446BEb18C9D5846bB';
  let gauge_relative_weight = '0x6207d866000000000000000000000000';

  const CRVprice = (await getPricesForAssets(['curve-dao-token']))['curve-dao-token'];

  const poolsGaugesAddresses = includeFactoryV2 ?
    getNonFactoPoolGauges().concat([...getFactoryV2Pools(), ...getFactoryCryptoPools()].map(({ addresses: { gauge } }) => gauge).filter((gauge) => !!gauge)) :
    getNonFactoPoolGauges();
  const allPools = includeFactoryV2 ? getNonFactoPools().concat(getFactoryV2Pools()).concat(getFactoryCryptoPools()) : getNonFactoPools();
  let weightCalls = poolsGaugesAddresses.map((gauge) => [gaugeController_address, gauge_relative_weight + gauge.slice(2)])

  let aggCallsWeights = await contract.multicall.methods.aggregate(weightCalls).call()
  let decodedWeights = aggCallsWeights[1].map((hex, i) => [weightCalls[i][0], contract.web3.eth.abi.decodeParameter('uint256', hex) / 1e18])

  let ratesCalls = poolsGaugesAddresses.map((gauge) => [
    [gauge, "0x180692d0"],
    [gauge, "0x17e28089"],
    [gauge, "0x18160ddd"],
  ])

  let aggRates = await contract.multicall.methods.aggregate(ratesCalls.flat()).call()
  let decodedRate = aggRates[1].map((hex) => contract.web3.eth.abi.decodeParameter('uint256', hex))
  let gaugeRates = decodedRate.filter((_, i) => i % 3 == 0).map((v) => v / 1e18)
  let workingSupplies = decodedRate.filter((_, i) => i % 3 == 1).map((v) => v / 1e18)

  // NOTE: DEFAULTING TO virtual_price = 1 FOR FACTORY POOLS FOR NOW, REVERTING OTHERWISE
  // Change `pools` to `allPools` to try and retrieve virtual_price for facto v2 pools
  let virtualPriceCalls = getNonFactoPools().filter(({ addresses: { swap } }) => !!swap).map(({ addresses: { swap } }) => [swap, "0xbb7b8b80"])
  let aggVirtualPrices = await contract.multicall.methods.aggregate(virtualPriceCalls).call()
  let decodedVirtualPrices = aggVirtualPrices[1].map((hex, i) => [virtualPriceCalls[i][0], contract.web3.eth.abi.decodeParameter('uint256', hex) / 1e18])

  const CRVAPYs = {};
  const CRVboosts = {};

  let i = 0;
  for (const w of decodedWeights) {
    const lcGaugeAddress = `0x${weightCalls[i][1].slice(34).toLowerCase()}`;
    const gaugeData = await getGaugeDataForAddress(lcGaugeAddress);
    let pool = allPools.find(({ addresses: { gauge } }) => gauge && gauge.toLowerCase() === lcGaugeAddress)
    const { id: poolId, addresses: { swap: swapAddress }, isGaugeKilled } = pool;
    let virtual_price = decodedVirtualPrices.find((v) => v[0].toLowerCase() == swapAddress.toLowerCase())?.[1] || 1;
    const _working_supply = await getPoolUsdFigure(workingSupplies[i], pool);

    let rate = (gaugeRates[i] * w[1] * 31536000 / _working_supply * 0.4) / virtual_price
    if (isGaugeKilled || gaugeData?.is_killed) rate = 0;
    let apy = rate * CRVprice * 100
    if (isNaN(apy)) apy = 0

    CRVAPYs[poolId] = [apy, apy * 2.5, 2.5];
    CRVboosts[poolId] = 2.5;

    i += 1;
  }

  const boosts = {};
  const futureBoosts = {};

  if (userAddress) {
    const wrapper = new GraphQLClient('https://api.thegraph.com/subgraphs/name/convex-community/crv-emissions')

    const QUERY = gql`
          {
            gaugeLiquidities(where: { user: "${userAddress.toLowerCase()}" }) {
                gauge {
                  id
                }
                originalBalance
                originalSupply
                workingBalance
                workingSupply
            }
          }
        `;

    let results;
    try {
      results = await wrapper.request(QUERY, {})
    } catch (err) {
      console.error('Caught error:', err)
    }
    if (results?.gaugeLiquidities) {
      results = results.gaugeLiquidities;

      const userGaugesAddresses = results.map(({ gauge: { id } }) => id.toLowerCase());

      for (let gaugeBoost of results) {
        const pool = allPools.find((pool) => pool.addresses.gauge && pool.addresses.gauge.toLowerCase() == gaugeBoost.gauge.id.toLowerCase())
        if (!pool) continue; // If the gauge has been retired

        boosts[pool.id] = gaugeBoost.workingBalance / (0.4 * gaugeBoost.originalBalance);
        futureBoosts[pool.id] = await calculateFutureBoost({ gaugeBoost, userAddress, userGaugesAddresses });
        CRVAPYs[pool.id].push(CRVAPYs[pool.id][0] * boosts[pool.id]);
      }
    }
  }

  return {
    CRVprice,
    CRVAPYs: {
      ...CRVAPYs,
      'factory-v2-23': [0, 0, 0],
    },
    CRVboosts,
    boosts,
    futureBoosts,
  };
}, {
  promise: true,
  maxAge: 10 * 60 * 1000, // 10m
  length: 2,
});

const getFactoCRVAPY = memoize(async () => {
  const hasSidechainCrv = !!config.factoSidechainGaugesCrvRewardsEndpoint;
  const CRVAPYs = {};
  const CRVboosts = {};
  const boosts = {};
  const futureBoosts = {};
  let CRVprice;

  if (hasSidechainCrv) {
    CRVprice = (await getPricesForAssets(['curve-dao-token']))['curve-dao-token'];
    const { data: { sideChainGaugesApys } } =
      await (await Request.get(config.factoSidechainGaugesCrvRewardsEndpoint)).json();

    sideChainGaugesApys.forEach(({ address, apy }) => {
      const pool = findPoolForSwapAddress(address);
      if (!pool) return;

      const boost = 1; // TODO add side boosties support
      CRVAPYs[pool.id] = [apy, apy * 2.5, 2.5, apy * boost];
    });
  }

  return { CRVprice, CRVAPYs, CRVboosts, boosts, futureBoosts };
}, {
  promise: true,
  maxAge: 30 * 1000, // 30s
  primitive: true,
});

const getGaugesTotalSupplies = memoize(async (gaugesAddress) => (
  multiCall(gaugesAddress.map((gaugeAddress) => ({
    address: gaugeAddress,
    abi: ERC20_abi,
    methodName: 'totalSupply',
  })))
), {
  promise: true,
  maxAge: 30 * 1000, // 30s
  primitive: true,
});

const getVecrvUserStats = memoize(async (userAddress) => {
  const [userVecrv, totalVecrv] = await multiCall([{
    address: votingescrow_address,
    abi: votingescrow_abi,
    methodName: 'balanceOf',
    params: [userAddress],
  }, {
    address: votingescrow_address,
    abi: votingescrow_abi,
    methodName: 'totalSupply',
  }]);

  return { userVecrv, totalVecrv };
}, {
  promise: true,
  maxAge: 30 * 1000, // 30s
});

const calculateFutureBoost = async ({
  gaugeBoost,
  userAddress,
  userGaugesAddresses,
}) => {
  const [
    { userVecrv, totalVecrv },
    userGaugesTotalSupplies,
  ] = await Promise.all([
    getVecrvUserStats(userAddress),
    getGaugesTotalSupplies(userGaugesAddresses),
  ]);

  const userGaugeBalance = gaugeBoost.originalBalance;
  const gaugeTotalSupply = userGaugesTotalSupplies[userGaugesAddresses.indexOf(gaugeBoost.gauge.id.toLowerCase())];

  const eq = (userGaugeBalance * 0.4) + (gaugeTotalSupply * userVecrv / totalVecrv * 0.6);
  const realWorkingBalance = Math.min(eq, userGaugeBalance);

  const futureBoost = realWorkingBalance / (0.4 * gaugeBoost.originalBalance);
  return futureBoost;
};

export default getCRVAPY;
export { getFactoCRVAPY };
